import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from '../api/axios';
import { BACKEND_URL } from '../config';
import { useAlert } from '../context/AlertContext';
import './Alerts.css';

const BASE_TYPES = [
  { key: 'follow',       label: 'Follow',       icon: '💜', group: 'Twitch', hasVariants: false },
  { key: 'subscription', label: 'Subscription', icon: '⭐', group: 'Twitch', hasVariants: true,  variantOptions: ['Tier 1','Tier 2','Tier 3','Prime'] },
  { key: 'resub',        label: 'Resub',        icon: '🔄', group: 'Twitch', hasVariants: true,  variantOptions: ['1-5 Monate','6-11 Monate','12+ Monate'] },
  { key: 'subgift',      label: 'Sub Gift',     icon: '🎁', group: 'Twitch', hasVariants: false },
  { key: 'raid',         label: 'Raid',         icon: '🚀', group: 'Twitch', hasVariants: true,  variantOptions: ['1-9 Viewer','10-49 Viewer','50+ Viewer'] },
  { key: 'host',         label: 'Host',         icon: '📺', group: 'Twitch', hasVariants: false },
  { key: 'cheer',        label: 'Bits',         icon: '💎', group: 'Bits',   hasVariants: true,
    variantOptions: ['1+ Bits','100+ Bits','500+ Bits','1.000+ Bits','10.000+ Bits'],
    variantKeys: ['cheer','cheer_100','cheer_500','cheer_1000','cheer_10000'] },
];

const ANIMATIONS = ['slideUp','slideDown','slideLeft','slideRight','zoomBounce','shake','explode','fireworks','confetti','glitch','flip','pulse'];
const ANIM_LABELS = { slideUp:'Slide Up',slideDown:'Slide Down',slideLeft:'Slide Left',slideRight:'Slide Right',zoomBounce:'Zoom Bounce',shake:'Shake',explode:'Explode',fireworks:'Fireworks',confetti:'Confetti',glitch:'Glitch',flip:'Flip',pulse:'Pulse' };

const PREVIEW_KF = [
  '@keyframes prev-slideUp{0%{transform:translateY(60px);opacity:0}60%{transform:translateY(-6px);opacity:1}100%{transform:translateY(0);opacity:1}}',
  '@keyframes prev-slideDown{0%{transform:translateY(-60px);opacity:0}60%{transform:translateY(6px);opacity:1}100%{transform:translateY(0);opacity:1}}',
  '@keyframes prev-slideLeft{0%{transform:translateX(60px);opacity:0}60%{transform:translateX(-6px);opacity:1}100%{transform:translateX(0);opacity:1}}',
  '@keyframes prev-slideRight{0%{transform:translateX(-60px);opacity:0}60%{transform:translateX(6px);opacity:1}100%{transform:translateX(0);opacity:1}}',
  '@keyframes prev-zoomBounce{0%{transform:scale(0);opacity:0}50%{transform:scale(1.15);opacity:1}75%{transform:scale(0.95)}100%{transform:scale(1);opacity:1}}',
  '@keyframes prev-shake{0%,100%{transform:rotate(0)}20%{transform:rotate(-6deg)}40%{transform:rotate(6deg)}60%{transform:rotate(-4deg)}80%{transform:rotate(4deg)}}',
  '@keyframes prev-explode{0%{transform:scale(0) rotate(-180deg);opacity:0}50%{transform:scale(1.2) rotate(10deg);opacity:1}100%{transform:scale(1) rotate(0);opacity:1}}',
  '@keyframes prev-fireworks{0%{transform:scale(0);opacity:0;filter:brightness(4)}40%{transform:scale(1.15);opacity:1;filter:brightness(2)}100%{transform:scale(1);opacity:1;filter:brightness(1)}}',
  '@keyframes prev-confetti{0%{transform:scale(0.7);opacity:0}60%{transform:scale(1.05);opacity:1}100%{transform:scale(1);opacity:1}}',
  '@keyframes prev-glitch{0%,100%{transform:translate(0)}20%{transform:translate(-4px,2px);filter:hue-rotate(90deg)}40%{transform:translate(4px,-2px);filter:hue-rotate(180deg)}60%{transform:translate(-2px,0);filter:none}}',
  '@keyframes prev-flip{0%{transform:perspective(400px) rotateY(-90deg);opacity:0}60%{transform:perspective(400px) rotateY(10deg);opacity:1}100%{transform:perspective(400px) rotateY(0);opacity:1}}',
  '@keyframes prev-pulse{0%{transform:scale(0.5);opacity:0}50%{transform:scale(1.1);opacity:1}75%{transform:scale(0.97)}100%{transform:scale(1);opacity:1}}'
].join('');

export default function Alerts() {
  const [configs, setConfigs] = useState({});
  const [selectedBase, setSelectedBase] = useState('follow');
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  const [soundFiles, setSoundFiles] = useState([]);
  const [imageFiles, setImageFiles] = useState([]);
  const [uploadingSound, setUploadingSound] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showUrlModal, setShowUrlModal] = useState(false);
  const [previewAnim, setPreviewAnim] = useState(false);
  const [previewPlaying, setPreviewPlaying] = useState(false);
  const soundInputRef = useRef();
  const imageInputRef = useRef();
  const audioRef = useRef(new Audio());
  const previewTimer = useRef();
  const notify = useAlert();

  useEffect(() => {
    const el = document.createElement('style');
    el.id = 'al-kf';
    el.textContent = PREVIEW_KF;
    document.head.appendChild(el);
    return () => el.remove();
  }, []);

  useEffect(() => { loadAll(); }, []);

  const loadAll = async () => {
    try {
      const [cfgRes, sndRes, imgRes] = await Promise.all([
        axios.get('/api/alerts/configs'),
        axios.get('/api/alerts/files/sounds'),
        axios.get('/api/alerts/files/images'),
      ]);
      setConfigs(cfgRes.data);
      setSoundFiles(sndRes.data);
      setImageFiles(imgRes.data);
    } catch (e) { notify.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };

  const currentKey = selectedVariant || selectedBase;
  const cfg = configs[currentKey] || configs[selectedBase] || {};
  const baseType = BASE_TYPES.find(t => t.key === selectedBase);

  const update = (field, value) =>
    setConfigs(prev => ({ ...prev, [currentKey]: { ...prev[currentKey], [field]: value } }));

  const save = async () => {
    try { await axios.post('/api/alerts/configs/' + currentKey, configs[currentKey]); notify.success('Gespeichert!'); }
    catch (e) { notify.error('Fehler'); }
  };

  const testOverlay = async () => {
    try { await axios.post('/api/alerts/test/' + currentKey); notify.success('Test gesendet!'); }
    catch (e) { notify.error('Fehler'); }
  };

  const playPreview = useCallback(() => {
    clearTimeout(previewTimer.current);
    setPreviewAnim(false);
    setTimeout(() => {
      setPreviewAnim(true);
      previewTimer.current = setTimeout(() => setPreviewAnim(false), cfg.duration || 5000);
    }, 50);
  }, [cfg.duration]);

  const previewSound = (url) => {
    if (!url) return;
    const full = url.startsWith('http') ? url : window.location.origin + url;
    audioRef.current.pause();
    audioRef.current.src = full;
    audioRef.current.volume = cfg.volume || 0.7;
    audioRef.current.play().catch(() => {});
    setPreviewPlaying(true);
    audioRef.current.onended = () => setPreviewPlaying(false);
  };

  const uploadSound = async (file) => {
    if (!file) return;
    setUploadingSound(true);
    const fd = new FormData(); fd.append('sound', file);
    try {
      const { data } = await axios.post('/api/alerts/upload/sound', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      update('sound', data.url);
      notify.success(file.name + ' hochgeladen!');
      const r = await axios.get('/api/alerts/files/sounds'); setSoundFiles(r.data);
    } catch (e) { notify.error('Upload fehlgeschlagen'); }
    finally { setUploadingSound(false); }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploadingImage(true);
    const fd = new FormData(); fd.append('image', file);
    try {
      const { data } = await axios.post('/api/alerts/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      update('image', data.url);
      notify.success(file.name + ' hochgeladen!');
      const r = await axios.get('/api/alerts/files/images'); setImageFiles(r.data);
    } catch (e) { notify.error('Upload fehlgeschlagen'); }
    finally { setUploadingImage(false); }
  };

  const deleteFile = async (type, name) => {
    try {
      await axios.delete('/api/alerts/files/' + type + '/' + name);
      if (type === 'sounds') { const r = await axios.get('/api/alerts/files/sounds'); setSoundFiles(r.data); }
      else { const r = await axios.get('/api/alerts/files/images'); setImageFiles(r.data); }
    } catch (e) { notify.error('Fehler'); }
  };

  const getVariantKey = (base, vi) => base.variantKeys ? base.variantKeys[vi] : base.key + '_v' + vi;

  const getVariantLabel = () => {
    if (!selectedVariant || !baseType) return '';
    if (baseType.variantKeys) {
      const idx = baseType.variantKeys.indexOf(selectedVariant);
      return (baseType.variantOptions || [])[idx] || selectedVariant;
    }
    const idx = (baseType.variantOptions || []).findIndex((_, i) => getVariantKey(baseType, i) === selectedVariant);
    return (baseType.variantOptions || [])[idx] || selectedVariant;
  };

  const previewMsg = (cfg.message || '')
    .replace('{username}', 'StreamerMax')
    .replace('{months}', '12')
    .replace('{bits}', currentKey.includes('10000') ? '10000' : currentKey.includes('1000') ? '1000' : currentKey.includes('500') ? '500' : currentKey.includes('100') ? '100' : '50')
    .replace('{viewers}', '150')
    .replace('{recipient}', 'LuckyViewer')
    .replace('{message}', 'Geiler Stream!');

  const overlayUrl = window.location.origin + '/alerts.html?backend=' + encodeURIComponent(BACKEND_URL);
  const groups = [...new Set(BASE_TYPES.map(t => t.group))];
  const previewStyle = previewAnim
    ? { animation: 'prev-' + (cfg.animation || 'slideUp') + ' 0.7s ease-out forwards' }
    : { opacity: 0 };

  if (loading) return (
    <div className="alerts-page">
      <div className="al-loading"><div className="al-spinner" /><p>Lade Alerts...</p></div>
    </div>
  );

  return (
    <div className="alerts-page">
      {/* TOP BAR */}
      <div className="al-topbar">
        <div>
          <h2>Stream Alerts</h2>
          <p>Konfiguriere Alerts mit Varianten, Sounds &amp; Animationen</p>
        </div>
        <div className="al-topbar-actions">
          <button className="al-btn-obs" onClick={() => setShowUrlModal(true)}>📺 OBS URL</button>
          <button className="al-btn-save" onClick={save}>💾 Speichern</button>
        </div>
      </div>

      {/* 3-COLUMN LAYOUT */}
      <div className="al-layout">

        {/* LEFT: Type list */}
        <div className="al-typelist">
          {groups.map(g => (
            <div key={g} className="al-typegroup">
              <span className="al-typegroup-label">{g}</span>
              {BASE_TYPES.filter(t => t.group === g).map(t => (
                <div key={t.key}>
                  <button
                    className={'al-typeitem' + (selectedBase === t.key && !selectedVariant ? ' active' : '')}
                    onClick={() => { setSelectedBase(t.key); setSelectedVariant(null); setActiveTab('general'); }}
                  >
                    <span className="al-typeitem-icon">{t.icon}</span>
                    <span className="al-typeitem-label">{t.label}</span>
                    <span className={'al-typeitem-dot ' + (configs[t.key]?.enabled === false ? 'off' : 'on')} />
                  </button>
                  {selectedBase === t.key && t.hasVariants && t.variantOptions && (
                    <div className="al-variants">
                      {t.variantOptions.map((vLabel, vi) => {
                        const vKey = getVariantKey(t, vi);
                        return (
                          <button key={vKey}
                            className={'al-variant-item' + (selectedVariant === vKey ? ' active' : '')}
                            onClick={() => { setSelectedVariant(vKey); setActiveTab('general'); }}
                          >
                            <span className="al-variant-dot" />
                            <span style={{flex:1}}>{vLabel}</span>
                            <span className={'al-typeitem-dot ' + (configs[vKey]?.enabled === false ? 'off' : 'on')} />
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* CENTER: Settings */}
        <div className="al-center">
          {/* Header */}
          <div className="al-settings-header">
            <div className="al-settings-title">
              <span>{baseType?.icon}</span>
              <strong>{baseType?.label}{selectedVariant ? ' — ' + getVariantLabel() : ''}</strong>
            </div>
            <label className="al-toggle">
              <input type="checkbox" checked={cfg.enabled !== false} onChange={e => update('enabled', e.target.checked)} />
              <span className="al-toggle-track"><span className="al-toggle-thumb" /></span>
              <span className="al-toggle-label">{cfg.enabled !== false ? 'Aktiv' : 'Aus'}</span>
            </label>
          </div>

          {/* Tabs */}
          <div className="al-tabs">
            {[{id:'general',label:'Allgemein'},{id:'layout',label:'Layout'},{id:'animation',label:'Animation'},{id:'sound',label:'Sound'},{id:'image',label:'Bild/GIF'}].map(tab => (
              <button key={tab.id} className={'al-tab' + (activeTab === tab.id ? ' active' : '')} onClick={() => setActiveTab(tab.id)}>
                {tab.label}
              </button>
            ))}
          </div>

          <div className="al-tab-body">

            {/* GENERAL */}
            {activeTab === 'general' && (
              <div className="al-fields">
                <div className="al-field al-field-full">
                  <label>Nachricht</label>
                  <input type="text" value={cfg.message || ''} onChange={e => update('message', e.target.value)} placeholder="{username} folgt jetzt!" />
                  <small>Variablen: <code>{'{username}'}</code> <code>{'{months}'}</code> <code>{'{bits}'}</code> <code>{'{viewers}'}</code> <code>{'{recipient}'}</code> <code>{'{message}'}</code></small>
                </div>
                <div className="al-field">
                  <label>Text-Farbe</label>
                  <div className="al-color-row">
                    <input type="color" value={cfg.text_color || '#FFD700'} onChange={e => update('text_color', e.target.value)} />
                    <input type="text" value={cfg.text_color || '#FFD700'} onChange={e => update('text_color', e.target.value)} />
                  </div>
                </div>
                <div className="al-field">
                  <label>Schriftgröße — <strong>{cfg.font_size || 48}px</strong></label>
                  <input type="range" min="20" max="96" value={cfg.font_size || 48} onChange={e => update('font_size', +e.target.value)} />
                </div>
                <div className="al-field">
                  <label>Anzeigedauer — <strong>{((cfg.duration || 5000) / 1000).toFixed(0)}s</strong></label>
                  <input type="range" min="2000" max="15000" step="500" value={cfg.duration || 5000} onChange={e => update('duration', +e.target.value)} />
                </div>
                <div className="al-field">
                  <label>Lautstärke — <strong>{Math.round((cfg.volume || 0.7) * 100)}%</strong></label>
                  <input type="range" min="0" max="1" step="0.05" value={cfg.volume || 0.7} onChange={e => update('volume', parseFloat(e.target.value))} />
                </div>
              </div>
            )}

            {/* LAYOUT */}
            {activeTab === 'layout' && (
              <div className="al-fields">
                {/* Position */}
                <div className="al-field al-field-full">
                  <label>Position auf dem Bildschirm</label>
                  <div className="al-position-grid">
                    {[
                      {v:'top-left',    label:'↖'},
                      {v:'top-center',  label:'↑'},
                      {v:'top-right',   label:'↗'},
                      {v:'mid-left',    label:'←'},
                      {v:'center',      label:'⊙'},
                      {v:'mid-right',   label:'→'},
                      {v:'bot-left',    label:'↙'},
                      {v:'bot-center',  label:'↓'},
                      {v:'bot-right',   label:'↘'},
                    ].map(p => (
                      <button key={p.v}
                        className={'al-pos-btn' + ((cfg.position || 'center') === p.v ? ' active' : '')}
                        onClick={() => update('position', p.v)}>
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Fine-tune offset */}
                <div className="al-field">
                  <label>X Versatz — <strong>{cfg.offset_x || 0}px</strong></label>
                  <input type="range" min="-500" max="500" step="5" value={cfg.offset_x || 0} onChange={e => update('offset_x', +e.target.value)} />
                  <div className="al-range-labels"><span>-500</span><span>0</span><span>+500</span></div>
                </div>
                <div className="al-field">
                  <label>Y Versatz — <strong>{cfg.offset_y || 0}px</strong></label>
                  <input type="range" min="-400" max="400" step="5" value={cfg.offset_y || 0} onChange={e => update('offset_y', +e.target.value)} />
                  <div className="al-range-labels"><span>-400</span><span>0</span><span>+400</span></div>
                </div>

                {/* Text alignment */}
                <div className="al-field">
                  <label>Text-Ausrichtung</label>
                  <div className="al-btn-group">
                    {['left','center','right'].map(a => (
                      <button key={a}
                        className={'al-group-btn' + ((cfg.text_align || 'center') === a ? ' active' : '')}
                        onClick={() => update('text_align', a)}>
                        {a === 'left' ? '⬅ Links' : a === 'center' ? '↔ Mitte' : 'Rechts ➡'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Image position relative to text */}
                <div className="al-field">
                  <label>Bild-Position</label>
                  <div className="al-btn-group">
                    {[{v:'above',l:'⬆ Über Text'},{v:'below',l:'⬇ Unter Text'},{v:'left',l:'⬅ Links'},{v:'right',l:'Rechts ➡'}].map(p => (
                      <button key={p.v}
                        className={'al-group-btn' + ((cfg.img_position || 'above') === p.v ? ' active' : '')}
                        onClick={() => update('img_position', p.v)}>
                        {p.l}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Image size */}
                <div className="al-field">
                  <label>Bild-Größe — <strong>{cfg.img_size || 300}px</strong></label>
                  <input type="range" min="50" max="600" step="10" value={cfg.img_size || 300} onChange={e => update('img_size', +e.target.value)} />
                </div>

                {/* Text background */}
                <div className="al-field">
                  <label>Text-Hintergrund</label>
                  <div className="al-btn-group">
                    {[{v:'none',l:'Keiner'},{v:'dark',l:'Dunkel'},{v:'blur',l:'Blur'},{v:'solid',l:'Solid'}].map(b => (
                      <button key={b.v}
                        className={'al-group-btn' + ((cfg.text_bg || 'dark') === b.v ? ' active' : '')}
                        onClick={() => update('text_bg', b.v)}>
                        {b.l}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Text shadow */}
                <div className="al-field">
                  <label>Text-Schatten</label>
                  <div className="al-btn-group">
                    {[{v:'none',l:'Kein'},{v:'soft',l:'Weich'},{v:'hard',l:'Hart'},{v:'glow',l:'Glow'}].map(s => (
                      <button key={s.v}
                        className={'al-group-btn' + ((cfg.text_shadow || 'hard') === s.v ? ' active' : '')}
                        onClick={() => update('text_shadow', s.v)}>
                        {s.l}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Font */}
                <div className="al-field">
                  <label>Schriftart</label>
                  <select className="al-select" value={cfg.font_family || 'Arial'} onChange={e => update('font_family', e.target.value)}>
                    <option value="Arial">Arial</option>
                    <option value="Impact">Impact</option>
                    <option value="'Segoe UI'">Segoe UI</option>
                    <option value="'Comic Sans MS'">Comic Sans</option>
                    <option value="Georgia">Georgia</option>
                    <option value="'Courier New'">Courier New</option>
                    <option value="Verdana">Verdana</option>
                    <option value="Tahoma">Tahoma</option>
                  </select>
                </div>

                {/* Font weight */}
                <div className="al-field">
                  <label>Schriftstärke</label>
                  <div className="al-btn-group">
                    {[{v:'400',l:'Normal'},{v:'700',l:'Fett'},{v:'900',l:'Extra Fett'}].map(w => (
                      <button key={w.v}
                        className={'al-group-btn' + ((cfg.font_weight || '900') === w.v ? ' active' : '')}
                        onClick={() => update('font_weight', w.v)}>
                        {w.l}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Letter spacing */}
                <div className="al-field">
                  <label>Buchstaben-Abstand — <strong>{cfg.letter_spacing || 0}px</strong></label>
                  <input type="range" min="-2" max="20" step="1" value={cfg.letter_spacing || 0} onChange={e => update('letter_spacing', +e.target.value)} />
                </div>

                {/* Line height */}
                <div className="al-field">
                  <label>Zeilenhöhe — <strong>{cfg.line_height || 1.3}</strong></label>
                  <input type="range" min="0.8" max="3" step="0.1" value={cfg.line_height || 1.3} onChange={e => update('line_height', parseFloat(e.target.value))} />
                </div>

              </div>
            )}

            {/* ANIMATION */}
            {activeTab === 'animation' && (
              <div className="al-fields">
                <div className="al-field al-field-full">
                  <label>Eingangs-Animation</label>
                  <div className="al-anim-grid">
                    {ANIMATIONS.map(a => (
                      <button key={a}
                        className={'al-anim-btn' + (cfg.animation === a ? ' active' : '')}
                        onClick={() => { update('animation', a); setTimeout(playPreview, 100); }}>
                        {ANIM_LABELS[a]}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="al-anim-details">
                  <div className="al-field">
                    <label>Animations-Geschwindigkeit</label>
                    <select className="al-select" value={cfg.anim_speed || 'normal'} onChange={e => update('anim_speed', e.target.value)}>
                      <option value="slow">Langsam (1.2s)</option>
                      <option value="normal">Normal (0.7s)</option>
                      <option value="fast">Schnell (0.4s)</option>
                      <option value="instant">Instant (0.2s)</option>
                    </select>
                  </div>
                  <div className="al-field">
                    <label>Ausgangs-Animation</label>
                    <select className="al-select" value={cfg.anim_out || 'fadeOut'} onChange={e => update('anim_out', e.target.value)}>
                      <option value="fadeOut">Fade Out</option>
                      <option value="slideOutUp">Slide Out Up</option>
                      <option value="slideOutDown">Slide Out Down</option>
                      <option value="zoomOut">Zoom Out</option>
                      <option value="none">Kein Ausgang</option>
                    </select>
                  </div>
                  <div className="al-field">
                    <label>Text-Animation</label>
                    <select className="al-select" value={cfg.text_anim || 'none'} onChange={e => update('text_anim', e.target.value)}>
                      <option value="none">Keine</option>
                      <option value="typewriter">Typewriter</option>
                      <option value="rainbow">Rainbow</option>
                      <option value="glow">Glow Pulse</option>
                      <option value="bounce-letters">Bounce Letters</option>
                    </select>
                  </div>
                  <div className="al-field">
                    <label>Hintergrund-Effekt</label>
                    <select className="al-select" value={cfg.bg_effect || 'none'} onChange={e => update('bg_effect', e.target.value)}>
                      <option value="none">Keiner</option>
                      <option value="particles">Partikel</option>
                      <option value="confetti">Konfetti</option>
                      <option value="sparks">Funken</option>
                      <option value="stars">Sterne</option>
                    </select>
                  </div>
                  <div className="al-field al-anim-detail-full">
                    <label>Custom CSS <small style={{textTransform:'none',letterSpacing:0,color:'#555'}}>(optional, für Profis)</small></label>
                    <textarea className="al-textarea" value={cfg.custom_css || ''} onChange={e => update('custom_css', e.target.value)} rows={5}
                      placeholder={'@keyframes myAnim {\n  from { opacity:0; transform:scale(0); }\n  to   { opacity:1; transform:scale(1); }\n}\n.alert-container { animation: myAnim 0.5s ease-out; }'} />
                    <small>Selektor: <code>.alert-container</code> · Wird direkt in den OBS-Overlay injiziert</small>
                  </div>
                </div>
              </div>
            )}

            {/* SOUND */}
            {activeTab === 'sound' && (
              <div className="al-fields">
                <div className="al-field al-field-full">
                  <label>Aktueller Sound</label>
                  <div className="al-media-row">
                    <input type="text" value={cfg.sound || ''} onChange={e => update('sound', e.target.value)} placeholder="Kein Sound" />
                    {cfg.sound && <button className="al-btn-icon" onClick={() => previewSound(cfg.sound)}>{previewPlaying ? '⏹' : '▶'}</button>}
                    {cfg.sound && <button className="al-btn-icon al-btn-del" onClick={() => update('sound', '')}>✕</button>}
                  </div>
                </div>
                <div className="al-field al-field-full">
                  <div className="al-dropzone"
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => { e.preventDefault(); uploadSound(e.dataTransfer.files[0]); }}
                    onClick={() => soundInputRef.current?.click()}>
                    <input ref={soundInputRef} type="file" accept=".mp3,.wav,.ogg,.m4a" style={{display:'none'}} onChange={e => uploadSound(e.target.files[0])} />
                    {uploadingSound ? <><div className="al-spinner"/><span>Hochladen...</span></> : <><span className="al-drop-icon">🔊</span><span>Sound hochladen</span><small>MP3 · WAV · OGG · M4A · max 10MB · Drag &amp; Drop</small></>}
                  </div>
                </div>
                {soundFiles.length > 0 && (
                  <div className="al-field al-field-full">
                    <label>Bibliothek ({soundFiles.length})</label>
                    <div className="al-file-list">
                      {soundFiles.map(f => (
                        <div key={f.name} className={'al-file-item' + (cfg.sound === f.url ? ' selected' : '')}>
                          <button className="al-file-pick" onClick={() => update('sound', f.url)}>
                            <span>🎵</span><span className="al-file-name">{f.name}</span>
                          </button>
                          <button className="al-btn-icon" onClick={() => previewSound(f.url)}>▶</button>
                          <button className="al-btn-icon al-btn-del" onClick={() => deleteFile('sounds', f.name)}>🗑</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* IMAGE */}
            {activeTab === 'image' && (
              <div className="al-fields">
                <div className="al-field al-field-full">
                  <label>Aktuelles Bild / GIF</label>
                  <div className="al-media-row">
                    <input type="text" value={cfg.image || ''} onChange={e => update('image', e.target.value)} placeholder="Kein Bild" />
                    {cfg.image && <button className="al-btn-icon al-btn-del" onClick={() => update('image', '')}>✕</button>}
                  </div>
                  {cfg.image && (
                    <div className="al-img-thumb">
                      <img src={cfg.image.startsWith('http') ? cfg.image : window.location.origin + cfg.image} alt="" onError={e => e.target.style.display='none'} />
                    </div>
                  )}
                </div>
                <div className="al-field al-field-full">
                  <div className="al-dropzone"
                    onDragOver={e => e.preventDefault()}
                    onDrop={e => { e.preventDefault(); uploadImage(e.dataTransfer.files[0]); }}
                    onClick={() => imageInputRef.current?.click()}>
                    <input ref={imageInputRef} type="file" accept=".gif,.png,.jpg,.jpeg,.webp" style={{display:'none'}} onChange={e => uploadImage(e.target.files[0])} />
                    {uploadingImage ? <><div className="al-spinner"/><span>Hochladen...</span></> : <><span className="al-drop-icon">🖼</span><span>Bild / GIF hochladen</span><small>GIF · PNG · JPG · WebP · max 20MB</small></>}
                  </div>
                </div>
                {imageFiles.length > 0 && (
                  <div className="al-field al-field-full">
                    <label>Bibliothek ({imageFiles.length})</label>
                    <div className="al-img-grid">
                      {imageFiles.map(f => (
                        <div key={f.name} className={'al-img-item' + (cfg.image === f.url ? ' selected' : '')} onClick={() => update('image', f.url)}>
                          <img src={window.location.origin + f.url} alt={f.name} onError={e => e.target.style.display='none'} />
                          <span>{f.name}</span>
                          <button className="al-img-del" onClick={e => { e.stopPropagation(); deleteFile('images', f.name); }}>🗑</button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>
        </div>

        {/* RIGHT: Preview */}
        <div className="al-preview-panel">
          <div className="al-preview-header">
            <span>Vorschau</span>
            <span style={{fontSize:'11px',color:'#444'}}>{cfg.animation || 'slideUp'}</span>
          </div>
          <div className="al-preview-screen">
            <div className="al-preview-inner" style={{
              ...previewStyle,
              flexDirection: cfg.img_position === 'left' || cfg.img_position === 'right' ? 'row' : 'column',
              textAlign: cfg.text_align || 'center',
            }}>
              {cfg.image && (cfg.img_position === 'above' || cfg.img_position === 'left' || !cfg.img_position) && (
                <img className="al-preview-img"
                  style={{ maxWidth: Math.round((cfg.img_size || 300) * 0.38) + 'px', maxHeight: Math.round((cfg.img_size || 300) * 0.38) + 'px' }}
                  src={cfg.image.startsWith('http') ? cfg.image : window.location.origin + cfg.image}
                  alt="" onError={e => e.target.style.display='none'} />
              )}
              <div className="al-preview-text"
                style={{
                  color: cfg.text_color || '#FFD700',
                  fontSize: Math.round((cfg.font_size || 48) * 0.38) + 'px',
                  fontFamily: cfg.font_family || 'Arial',
                  fontWeight: cfg.font_weight || '900',
                  letterSpacing: (cfg.letter_spacing || 0) + 'px',
                  lineHeight: cfg.line_height || 1.3,
                  textAlign: cfg.text_align || 'center',
                  background: cfg.text_bg === 'none' ? 'none' : cfg.text_bg === 'blur' ? 'rgba(0,0,0,0.2)' : cfg.text_bg === 'solid' ? 'rgba(0,0,0,0.8)' : 'rgba(0,0,0,0.3)',
                  textShadow: cfg.text_shadow === 'none' ? 'none' : cfg.text_shadow === 'soft' ? '0 2px 8px rgba(0,0,0,0.5)' : cfg.text_shadow === 'glow' ? '0 0 20px ' + (cfg.text_color || '#FFD700') : '2px 2px 4px rgba(0,0,0,0.9), 0 0 10px rgba(0,0,0,0.8)',
                }}>
                {previewMsg || (baseType?.icon + ' ' + baseType?.label + ' Alert')}
              </div>
              {cfg.image && (cfg.img_position === 'below' || cfg.img_position === 'right') && (
                <img className="al-preview-img"
                  style={{ maxWidth: Math.round((cfg.img_size || 300) * 0.38) + 'px', maxHeight: Math.round((cfg.img_size || 300) * 0.38) + 'px' }}
                  src={cfg.image.startsWith('http') ? cfg.image : window.location.origin + cfg.image}
                  alt="" onError={e => e.target.style.display='none'} />
              )}
            </div>
            {!previewAnim && <div className="al-preview-idle">▶ Vorschau klicken</div>}
          </div>
          <div className="al-preview-footer">
            <div className="al-preview-meta">
              <span>⏱ {((cfg.duration || 5000) / 1000).toFixed(0)}s</span>
              <span>🔊 {Math.round((cfg.volume || 0.7) * 100)}%</span>
              <span>📐 {cfg.font_size || 48}px</span>
              {selectedVariant && <span className="al-variant-badge">{getVariantLabel()}</span>}
            </div>
            <div className="al-preview-btns">
              <button className="al-btn-preview" onClick={playPreview}>▶ Vorschau</button>
              <button className="al-btn-test" onClick={testOverlay}>🧪 OBS Test</button>
            </div>
          </div>
        </div>

      </div>

      {/* OBS Modal */}
      {showUrlModal && (
        <div className="modal-overlay" onClick={() => setShowUrlModal(false)}>
          <div className="al-obs-modal" onClick={e => e.stopPropagation()}>
            <h3>📺 OBS Browser Source URL</h3>
            <div className="al-obs-url">
              <input type="text" value={overlayUrl} readOnly onClick={e => e.target.select()} />
              <button onClick={() => { navigator.clipboard.writeText(overlayUrl); notify.success('Kopiert!'); }}>📋 Kopieren</button>
            </div>
            <ol className="al-obs-steps">
              <li>OBS → Quellen → <strong>+</strong> → Browser</li>
              <li>URL oben einfügen</li>
              <li>Breite: <strong>1920</strong> · Höhe: <strong>1080</strong></li>
              <li>✅ "Quelle schließen wenn nicht sichtbar"</li>
            </ol>
            <button className="al-btn-close" onClick={() => setShowUrlModal(false)}>Schließen</button>
          </div>
        </div>
      )}
    </div>
  );
}
